import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "@nexus/ui";

/** Panel width — also drives the clamp that keeps it on screen near the viewport edges. */
const CARD_WIDTH_PX = 244;
const CARD_HALF_WIDTH_PX = CARD_WIDTH_PX / 2;
/** Distance from the anchored point to the panel edge, clearing an enlarged dot or header. */
const ANCHOR_OFFSET_PX = 16;
/** Breathing room kept between the panel and the viewport edges. */
const VIEWPORT_MARGIN_PX = 8;
/** Caret square; half of it overlaps the panel edge so the tip reads as one shape. */
const CARET_SIZE_PX = 10;

export type HoverPanelPlacement = "top" | "bottom";

/** Where a panel points: a viewport-space point on its trigger, and which side to open on. */
export interface HoverPanelAnchor {
  x: number;
  y: number;
  placement: HoverPanelPlacement;
}

export interface HoverPanelProps {
  /** Referenced by the trigger's `aria-controls`. */
  id: string;
  anchor: HoverPanelAnchor;
  /** Accessible name — required for interactive panels, unused by decorative ones. */
  label?: string;
  /**
   * Interactive panels take pointer events and announce as a dialog; decorative ones stay
   * click-through and hidden from assistive tech, since their trigger already says it all.
   */
  isInteractive?: boolean;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  onEscape?: () => void;
  children: ReactNode;
}

/**
 * Hover panel pinned to a point on its trigger — a score-rail dot, a heatmap column header.
 * Unlike a tooltip it can hold interactive content (a cluster's creatives are clickable), so
 * it renders as a non-modal dialog that stays next to its trigger in DOM order, keeping it
 * reachable by Tab. Viewport-anchored rather than absolutely placed, so panels escape the
 * scroll and card clipping around their trigger; callers re-measure the anchor while the page
 * scrolls or resizes (see `useHoverPanelAnchor`).
 */
export function HoverPanel({
  id,
  anchor,
  label,
  isInteractive = false,
  onPointerEnter,
  onPointerLeave,
  onEscape,
  children,
}: HoverPanelProps) {
  const isTop = anchor.placement === "top";
  const edgeInset = CARD_HALF_WIDTH_PX + VIEWPORT_MARGIN_PX;
  const left = `clamp(${edgeInset}px, ${anchor.x}px, calc(100vw - ${edgeInset}px))`;
  const edge = isTop
    ? { bottom: `calc(100vh - ${anchor.y - ANCHOR_OFFSET_PX}px)` }
    : { top: `${anchor.y + ANCHOR_OFFSET_PX}px` };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    onEscape?.();
  };

  return (
    <>
      <div
        id={id}
        role={isInteractive ? "dialog" : undefined}
        aria-label={isInteractive ? label : undefined}
        aria-hidden={!isInteractive}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
        className={cn(
          "bg-popover text-popover-foreground border-border motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 fixed z-50 -translate-x-1/2 overflow-hidden rounded-md border shadow-lg outline-none motion-safe:duration-150 motion-safe:ease-out",
          isTop
            ? "origin-bottom motion-safe:slide-in-from-bottom-1"
            : "origin-top motion-safe:slide-in-from-top-1",
          isInteractive ? "pointer-events-auto" : "pointer-events-none",
        )}
        style={{
          ...edge,
          left,
          width: CARD_WIDTH_PX,
          maxWidth: `calc(100vw - ${VIEWPORT_MARGIN_PX * 2}px)`,
        }}
      >
        {children}
      </div>
      <span
        aria-hidden
        className={cn(
          "bg-popover border-border pointer-events-none fixed z-50 -translate-x-1/2 rotate-45",
          isTop
            ? "translate-y-1/2 border-r border-b"
            : "-translate-y-1/2 border-t border-l",
        )}
        style={{
          ...edge,
          left: `${anchor.x}px`,
          width: CARET_SIZE_PX,
          height: CARET_SIZE_PX,
        }}
      />
    </>
  );
}
